import { memo, useEffect, useRef, useState } from 'react';
import { type FieldType, IField, type IFieldOption as Option } from '@baishuyun/types';
import { FieldTypeNameMap } from '../const/ui';
import LineChecker from './line-checker';
import { OptionItem } from './option-item';
import { FieldIcon } from './field-icon';

type Field = IField;

export interface FieldCheckerProps {
  field: IField;
  isSubField?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  onChange?: (checked: boolean, field: Field, parentFieldName?: string, index?: number) => void;
  value?: unknown;
  checked?: boolean;
  streaming?: boolean;
  confirmed?: boolean;
  onLoaded?: (field: Field) => void;
}

export const FieldChecker = memo(
  ({
    field,
    disabled,
    isSubField,
    onChange,
    onLoaded,
    icon,
    value,
    checked,
    streaming,
    confirmed,
  }: FieldCheckerProps) => {
    const { widget, label } = field;
    if (!widget) {
      return null;
    }

    const { type } = widget;

    const t = type as FieldType;

    if (!FieldTypeNameMap.hasOwnProperty(type)) {
      return null;
    }

    const typeDesc = FieldTypeNameMap[t] || FieldTypeNameMap.unknown;

    const isSubForm = type === 'subform';

    const items = isSubForm ? [] : (widget.items as Option[]) || [];

    // --- subform sub-field state management ---
    const subFields = isSubForm ? (widget.items as Field[]) : [];
    const [subCheckedMap, setSubCheckedMap] = useState<Record<number, boolean>>(() => {
      const result: Record<number, boolean> = {};
      subFields.forEach((_, i) => {
        result[i] = true;
      });
      return result;
    });

    // When parent checked prop changes from outside, sync all sub-fields
    const prevChecked = useRef(checked);
    useEffect(() => {
      if (isSubForm && checked !== prevChecked.current) {
        prevChecked.current = checked;
        if (checked !== undefined) {
          const result: Record<number, boolean> = {};
          subFields.forEach((_, i) => {
            result[i] = checked;
          });
          setSubCheckedMap(result);
        }
      }
    }, [checked, isSubForm, subFields.length]);

    const handleSubFieldChange = (subChecked: boolean, subField: Field, index: number) => {
      setSubCheckedMap((prev) => {
        const next = { ...prev, [index]: subChecked };
        // If all sub-fields are now unchecked, uncheck the parent subform
        const allChecked = Object.values(next).every(Boolean);
        if (allChecked) {
          onChange?.(true, field);
        }

        const allUnchecked = Object.values(next).every((v) => v === false);
        if (allUnchecked) {
          onChange?.(false, field);
        }

        return next;
      });
      onChange?.(subChecked, subField, field.widget?.widgetName, index);
    };
    // --- end subform state management ---

    useEffect(() => {
      onLoaded?.(field);
    }, []);

    const handleCheck = (newChecked: boolean) => {
      onChange?.(newChecked, field);
    };

    const extraPadding = isSubField ? 'pl-[38px]' : '';

    // 表单填充
    if (value) {
      return (
        <LineChecker
          title={label}
          disabled={disabled}
          checked={checked}
          defaultChecked={true}
          onCheckedChange={handleCheck}
          icon={icon}
          className={extraPadding}
          shortDesc={value as string}
        />
      );
    }

    return (
      <>
        <LineChecker
          streaming={streaming}
          confirmed={confirmed}
          title={label}
          disabled={disabled}
          checked={checked}
          defaultChecked={true}
          onCheckedChange={handleCheck}
          icon={icon}
          shortDesc={typeDesc}
          className={extraPadding}
          extra={
            items.length ? (
              <>
                {items.map(({ value, text, color }) => (
                  <OptionItem key={value} color={color ? 'white' : undefined} bgColor={color}>
                    {text}
                  </OptionItem>
                ))}
              </>
            ) : null
          }
        />
        {isSubForm
          ? subFields.map((subField: Field, index: number) => (
              <FieldChecker
                confirmed={confirmed}
                streaming={streaming}
                key={subField.label}
                isSubField={true}
                disabled={disabled}
                checked={subCheckedMap[index]}
                field={subField}
                icon={<FieldIcon type={subField.widget?.type!} />}
                onChange={(subChecked) => {
                  handleSubFieldChange(subChecked, subField, index);
                }}
              />
            ))
          : null}
      </>
    );
  },
  (p1, p2) => {
    return (
      p1.field?.label === p2.field?.label &&
      p1.disabled === p2.disabled &&
      p1.checked === p2.checked &&
      p1.confirmed === p2.confirmed &&
      p1.streaming === p2.streaming
    );
  }
);
